"use client";

import { focusWithinRing } from "@decibeltrade/web-core/recipes";
import { forwardRef, JSX, useId, useLayoutEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { css, cx } from "styled-system/css";

import { useDebounce } from "@/hooks/use-debounce";
import { formControl, FormControlVariants } from "@/recipes/form-control";

import { clampOnBlur, isAllowedValue } from "./numeric-input-utils";

type InputAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "color" | "height" | "width" | "size"
>;

export interface InputProps extends InputAttributes {
  /** The size of the input */
  size?: FormControlVariants["size"];
  /** The validation state of the input */
  state?: FormControlVariants["state"];
  /** A label to be displayed inline with the input */
  inlineLabel?: string;
  /** An element to be positioned to the left of the input text */
  leadElement?: JSX.Element;
  /** An element to be positioned to the right of the input text */
  tailElement?: JSX.Element;
  /** Class name for applying additional styles to the input's wrapper element */
  className?: string;
  /** input component classname */
  inputClassName?: string;
}

/**
 * An element for accepting text-based input from the user.
 * It accepts all the attributes that an `input` element does (except color, height, and width).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = "md",
      state = "default",
      inlineLabel,
      leadElement,
      tailElement,
      className,
      onMouseDown,
      onFocus,
      onBlur,
      id,
      inputClassName,
      autoComplete = "off",
      ...props
    },
    ref,
  ) => {
    const localId = useId();
    const resolvedId = id ?? localId;
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const wasClicked = useRef(false);

    // Ever so slightly debounce the keyboard focus flag to prevent the focus ring from flashing
    // during the single render between the focus handler and mouse down handler's completion
    const debouncedIsKeyboardFocus = useDebounce(isKeyboardFocus, 10);

    // Merge the forwarded ref with our internal ref
    const mergedRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    return (
      <div
        onClick={() => {
          // Need this so that onFocus handler doesnt takes this as a keyboard focus
          wasClicked.current = true;
          inputRef.current?.focus();
          window.setTimeout(() => {
            wasClicked.current = false;
          }, 200);
        }}
        className={cx(
          formControl({ size, state }),
          css({ desktopDown: { flexDirection: "column", gap: "4!" } }),
          debouncedIsKeyboardFocus && focusWithinRing({ rounded: "350" }),
          className,
        )}
      >
        {!!inlineLabel && (
          <label
            htmlFor={resolvedId}
            className={css({
              alignSelf: "flex-start",
              textStyle: { base: "body.lg", desktop: "body.md" },
              color: "text.tertiary",
            })}
          >
            {inlineLabel}
          </label>
        )}
        <div
          className={css({ display: "flex", alignItems: "center", gap: "8", w: "full", minW: "0" })}
        >
          {!!leadElement && leadElement}
          <input
            ref={mergedRef}
            id={resolvedId}
            onMouseDown={(e) => {
              // Wait until next tick to set keyboard focus to false to prevent a race condition with
              // the focus handler that initially sets it to true
              window.setTimeout(() => setIsKeyboardFocus(false), 0);
              onMouseDown?.(e);
            }}
            onFocus={(e) => {
              // Assume the focus was initiated by a keyboard until a mouse down handler proves it false
              if (!wasClicked.current) setIsKeyboardFocus(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsKeyboardFocus(false);
              onBlur?.(e);
            }}
            className={cx(css({ flex: "1", minW: "0" }), inputClassName)}
            autoComplete={autoComplete}
            {...props}
          />
          {!!tailElement && <div onClick={(e) => e.stopPropagation()}>{tailElement}</div>}
        </div>
      </div>
    );
  },
);
Input.displayName = "Input";

export interface NumericInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "type" | "defaultValue"
> {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  decimalScale?: number;
  allowNegative?: boolean;
  prefix?: string;
  suffix?: string;
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  ({ value, onChange, decimalScale, allowNegative, prefix, suffix, ...props }, ref) => {
    const { onBlur: callerOnBlur, ...restProps } = props;
    const latestValueRef = useRef(value);
    useLayoutEffect(() => {
      latestValueRef.current = value;
    });

    return (
      <NumericFormat
        getInputRef={ref}
        customInput={Input}
        value={value === undefined ? "" : value}
        onValueChange={(v) => {
          latestValueRef.current = v.floatValue;
          onChange(v.floatValue);
        }}
        thousandSeparator
        allowedDecimalSeparators={[".", ","]}
        inputMode="decimal"
        valueIsNumericString
        allowNegative={allowNegative}
        decimalScale={decimalScale}
        prefix={prefix}
        suffix={suffix}
        isAllowed={(values) => isAllowedValue(values.floatValue, restProps.max, decimalScale)}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          clampOnBlur(latestValueRef.current, restProps.min, restProps.max, onChange);
          callerOnBlur?.(e);
        }}
        {...restProps}
      />
    );
  },
);
NumericInput.displayName = "NumericInput";
